import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import DashboardService from 'api/dashboard/DashboardService';
import { FleetByFuelDashboardDto, FleetByFuelItem } from 'api/dashboard/dtos/FleetByFuelDashboardDto';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import Box from 'common/components/box/Box';
import useInfiniteReveal from '../components/useInfiniteReveal';
import InlineKpiCounter from '../components/InlineKpiCounter';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import styles from './VehiclesByFuel.module.scss';
import infiniteStyles from '../components/useInfiniteReveal.module.scss';

const COLOR_SCALE = [
	'#025a7a', // darkest — top rank
	'#038cb6',
	'#02c7c7',
	'#6de0e0',
	'#b8f0f0', // lightest — 5th+
];
const PAGE_SIZE = 10;

const VehiclesByFuel = () => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<FleetByFuelDashboardDto | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const result = await DashboardService.getFleetByFuel({});
				if (!cancelled) setData(result);
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, []);

	if (!data && !isLoading) return null;

	if (!data?.items || data.items.length === 0) {
		return (
			<Box className={tableStyles.container} title={t('dashboard.vehicles_by_fuel') as string} subTitleTooltip={t('dashboard.vehicles_by_fuel_tooltip' as any) as string}>
				{isLoading
					? <div className={styles.centerAlign}><LoadingSpinner /></div>
					: <WidgetNoData />
				}
			</Box>
		);
	}

	const translateFuelName = (name: string) => {
		const key = `vehicle.fuels.${name}` as any;
		const translated = t(key) as string;
		return translated !== key ? translated : name.charAt(0) + name.slice(1).toLowerCase();
	};

	return <VehiclesByFuelList data={data.items} totalVehicles={data.totalVehicles} isLoading={isLoading} translateFuelName={translateFuelName} />;
};

type ListProps = {
	data: FleetByFuelItem[];
	totalVehicles: number;
	isLoading: boolean;
	translateFuelName: (name: string) => string;
};

const VehiclesByFuelList = ({ data, totalVehicles, isLoading, translateFuelName }: ListProps) => {
	const { t } = useTranslation();
	const sumAssignments = data.reduce((sum, item) => sum + item.count, 0);
	const maxCount = Math.max(...data.map(item => item.count));
	const { visibleItems, sentinelRef, hasMore } = useInfiniteReveal(data, PAGE_SIZE);

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.vehicles_by_fuel') as string}
			subTitleTooltip={t('dashboard.vehicles_by_fuel_tooltip' as any) as string}
			renderOptions={<InlineKpiCounter value={String(totalVehicles)} subLabel={t('dashboard.total_assets' as any) as string} />}
		>
			<div className={styles.chartContainer}>
				{isLoading ? <div className={styles.centerAlign}><LoadingSpinner /></div> : (
					<>
						<div className={styles.list}>
						{visibleItems.map((item, i) => {
							const pct = sumAssignments > 0 ? Math.round((item.count / sumAssignments) * 100) : 0;
							const barWidth = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
							const color = COLOR_SCALE[Math.min(i, COLOR_SCALE.length - 1)];
							return (
								<div key={i} className={styles.row}>
									<div className={styles.rowHeader}>
										<div className={styles.labelGroup}>
											<span className={styles.dot} style={{ backgroundColor: color }} />
											<span className={styles.label}>{translateFuelName(item.fuelName)}</span>
										</div>
										<div className={styles.valueGroup}>
											<span className={styles.value}>{item.count}</span>
											<span className={styles.pct}>{pct}%</span>
										</div>
									</div>
									<div className={styles.barTrack}>
										<div className={styles.barFill} style={{ width: `${barWidth}%`, backgroundColor: color }} />
									</div>
								</div>
							);
						})}
						{hasMore && <div ref={sentinelRef} className={infiniteStyles.sentinel} />}
						</div>
					</>
				)}
			</div>
		</Box>
	);
};

export default VehiclesByFuel;
